import { useState, useRef, useEffect } from "react";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import { Search } from "@mui/icons-material";
import CancelIcon from "@mui/icons-material/Cancel";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { debounce } from "lodash";
import { Position } from "@/types";
import { ScannerInputValue } from "@/interfaces";
import { isDigit, isFunction } from "@/utils";
import { colors } from "@/theme";

export type ScannerInputProps = {
  codeLength?: number | number[];
  wait?: number;
  onChange?: (value?: ScannerInputValue) => void;
  fullWidth?: boolean;
  placeholder?: string;
  searchIcon?: boolean;
  searchIconPosition?: Position;
  disabled?: boolean;
  autoFocus?: boolean;
  testFn?: (str: string) => boolean;
  searchValue?: ScannerInputValue;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

interface ScannerInputState {
  value?: string;
  lastValue?: string;
}

const checkCode = (
  value?: string,
  lastValue?: string,
  codeLength?: number | number[],
  testFn?: (str: string) => boolean
) => {
  const isNotCodeResult = { isCode: false, code: undefined };

  if (!codeLength) {
    return isNotCodeResult;
  }

  if (!Array.isArray(codeLength)) {
    codeLength = [codeLength];
  }

  for (let i = 0, l = codeLength.length; i < l; ++i) {
    let code,
      _codeLength = codeLength[i];

    const isCode =
      !!value &&
      value.length - (lastValue?.length || 0) === _codeLength &&
      testFn?.((code = value.slice(-_codeLength)));

    if (isCode) {
      return { isCode, ...(isCode && { code }) };
    }
  }

  return isNotCodeResult;
};

const ScannerInput: React.FC<ScannerInputProps> = ({
  codeLength = undefined,
  wait = undefined,
  onChange = undefined,
  fullWidth = false,
  placeholder = undefined,
  searchIcon = false,
  searchIconPosition = "start",
  disabled = undefined,
  autoFocus = undefined,
  testFn = isDigit,
  searchValue = undefined,
  onKeyPress = undefined,
}) => {
  const initialState = { value: "", lastValue: "" };
  const [state, setState] = useState<ScannerInputState | null | undefined>({
    ...initialState,
  });
  const [showClearIcon, setShowClearIcon] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const delayCall = useRef(
    debounce(
      (
        value?: string,
        state?: ScannerInputState | null,
        setState?: Function,
        codeLength?: number | number[],
        onChange?: Function
      ) => {
        const { isCode, code } = checkCode(
          value,
          state?.lastValue,
          codeLength,
          testFn
        );

        if (isFunction(setState)) {
          if (isCode) {
            setState?.({ value: code, lastValue: code });
          } else {
            setState?.({ lastValue: value });
          }
        }

        if (isFunction(onChange)) {
          onChange?.({ isCode, value: isCode ? code : value });
        }
      },
      wait
    )
  ).current;

  const handleClear = () => {
    setState({ ...initialState });
    setShowClearIcon(false);
    if (isFunction(onChange)) {
      onChange?.({ isCode: false, value: "" });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    setShowClearIcon(!!value);
    setState({ value });
    delayCall(value, state, setState, codeLength, onChange);
  };

  useEffect(() => {
    return () => {
      delayCall.cancel();
    };
  }, [delayCall]);

  useEffect(() => {
    if (!searchValue) {
      return;
    }

    if (!searchValue.value && (!!state?.value || !!state?.lastValue)) {
      setState({ ...initialState });
      setShowClearIcon(false);
    }
  }, [searchValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  return (
    <TextField
      inputRef={inputRef}
      value={state?.value}
      variant="filled"
      color="success"
      onChange={handleChange}
      onKeyPress={onKeyPress}
      fullWidth={fullWidth}
      placeholder={placeholder}
      disabled={disabled}
      autoFocus={autoFocus}
      slotProps={{
        input: {
          ...(searchIcon &&
            !!searchIconPosition && {
              startAdornment:
                searchIconPosition === "start" ? (
                  <InputAdornment
                    position="start"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <BadgeOutlinedIcon
                      sx={{ color: colors.primaryOzone500, fontSize: 35 }}
                    />
                  </InputAdornment>
                ) : null,
              endAdornment: (
                <InputAdornment
                  position="end"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  {showClearIcon && (
                    <IconButton
                      onClick={handleClear}
                      size="small"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: "15px",
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <CancelIcon sx={{ color: colors.primaryOzone500, fontSize: 35 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }),
        },
      }}
      sx={{
        "& .MuiInputBase-root": {
          backgroundColor: colors.white,
          "font-family": "PF DIN Text Pro",
          "font-size": "44px",
          "font-style": "normal",
          "line-height": "16px",
          "&:hover": {
            backgroundColor: colors.white,
          },
          "&.Mui-focused": {
            backgroundColor: colors.white,
          },
          "& .MuiInputBase-input": {
            color: colors.primaryOzone500,
            textAlign: "center",
          },
          "& .MuiAutocomplete-input": {
            color: colors.primaryOzone500,
          },
          "&::before": {
            borderBottom: `1px solid ${colors.neutralGrey700} !important`,
          },
          "&:hover:not(.Mui-disabled):before": {
            borderBottom: `1px solid ${colors.neutralGrey400} !important`,
          },
          "&.Mui-focused:after": {
            borderBottom: `1px solid ${colors.neutralGrey400} !important`,
          },
        },
        "& .MuiInputBase-input": {
          color: colors.primaryOzone500,
          textAlign: "center",
          "&::placeholder": {
            color: colors.neutralGrey600,
            opacity: 1,
            textAlign: "center",
            fontWeight: "normal",
          },
        },
        // Handle autocomplete styling without affecting placeholder
        "& input:-webkit-autofill": {
          WebkitTextFillColor: `${colors.primaryOzone500} !important`,
          transition: "background-color 5000s ease-in-out 0s",
          WebkitBoxShadow: `0 0 0 100px ${colors.white} inset !important`,
        },
        "& input:-webkit-autofill:focus": {
          WebkitTextFillColor: `${colors.primaryOzone500} !important`,
          WebkitBoxShadow: `0 0 0 100px ${colors.white} inset !important`,
        },
        "& input.MuiInputBase-input:-internal-autofill-selected": {
          WebkitTextFillColor: `${colors.primaryOzone500} !important`,
          backgroundColor: `${colors.white} !important`,
        },
      }}
      // Add inline style for the input element without affecting placeholder
      inputProps={{
        style: { 
          color: colors.primaryOzone500,
        },
        // Only apply WebkitTextFillColor to selected/filled values
        sx: {
          "&:-webkit-autofill": {
            WebkitTextFillColor: `${colors.primaryOzone500} !important`,
          }
        }
      }}
    />
  );
};

export default ScannerInput;